import { SchmancyElement } from '@schmancy/mixins/index'
import { color } from '@schmancy/directives'
import { css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { SchmancyEvents } from '@schmancy/types'
import { SchmancyTheme } from '@schmancy/theme'
import { BehaviorSubject, fromEvent, Subject } from 'rxjs'
import { takeUntil, throttleTime, tap, pairwise, map, filter } from 'rxjs/operators'
import type { SchmancyNavigationBarItem } from './navigation-bar-item'
import { theme } from '@schmancy/theme/theme.service'

const NAV_BAR_HEIGHT = 80

/** Matches Tailwind's md: breakpoint. */
const MOBILE_BREAKPOINT = '(max-width: 767px)'

/**
 * Material Design 3 horizontal navigation, 3-7 destinations. Automatically hides in
 * fullscreen mode via schmancyTheme.next({ fullscreen: true }).
 *
 * Carries `z-10` by default; the consumer positions it (e.g. `class="fixed bottom-0 left-0 right-0"`).
 *
 * @element schmancy-navigation-bar
 * @slot - Default slot for navigation bar items
 *
 * @fires navigation-change - When an item is selected
 *
 * @example
 * <!-- Fixed at bottom (typical usage) -->
 * <schmancy-navigation-bar activeIndex="0" class="fixed bottom-0 left-0 right-0">
 *   <schmancy-navigation-bar-item icon="home" label="Home"></schmancy-navigation-bar-item>
 *   <schmancy-navigation-bar-item icon="search" label="Search"></schmancy-navigation-bar-item>
 *   <schmancy-navigation-bar-item icon="favorite" label="Favorites"></schmancy-navigation-bar-item>
 *   <schmancy-navigation-bar-item icon="settings" label="Settings"></schmancy-navigation-bar-item>
 * </schmancy-navigation-bar>
 *
 * @example
 * <!-- Within a container (demo/mockup usage) -->
 * <div class="flex flex-col h-screen">
 *   <main class="flex-1">Content</main>
 *   <schmancy-navigation-bar activeIndex="0">
 *     <schmancy-navigation-bar-item icon="home" label="Home"></schmancy-navigation-bar-item>
 *   </schmancy-navigation-bar>
 * </div>
 */
@customElement('schmancy-navigation-bar')
export class SchmancyNavigationBar extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				transition: transform 0.3s ease-in-out;
			}

			:host([hide-on-scroll]) {
				transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
			}

			::slotted(schmancy-navigation-bar-item) {
				flex: 1;
				max-width: 168px;
			}

			:host(:focus-within) {
				outline: 2px solid var(--schmancy-sys-color-primary-default);
				outline-offset: -2px;
			}
		`,
	]
	private activeIndex$ = new BehaviorSubject<number>(-1)

	@property({ type: Number })
	get activeIndex() {
		return this.activeIndex$.value
	}
	set activeIndex(value: number) {
		this.activeIndex$.next(value)
	}

	@property({ type: Boolean, reflect: true })
	hideLabels = false

	@property({ type: Number, reflect: true })
	elevation = 2

	@property({ type: Boolean, reflect: true })
	hideOnScroll = false

	@state()
	private focusedIndex = -1

	@state()
	private isHiddenByScroll = false

	@state()
	private isFullscreen = false

	private getItems(): SchmancyNavigationBarItem[] {
		const slot = this.shadowRoot?.querySelector('slot') as HTMLSlotElement | null
		if (!slot) return []
		return slot
			.assignedElements({ flatten: true })
			.filter((el): el is SchmancyNavigationBarItem => el.tagName.toLowerCase() === 'schmancy-navigation-bar-item')
	}

	private readonly SCROLL_THRESHOLD = 10

	private mobileMediaQuery: MediaQueryList | null = null

	private visibility$ = new Subject<boolean>()

	private isMobileViewport(): boolean {
		return this.mobileMediaQuery?.matches ?? false
	}

	/** Reserves layout space for schmancy-page/fullHeight consumers below the bar. */
	private updateBottomOffset() {
		const shouldSetOffset = !this.isFullscreen && this.isMobileViewport()
		theme.setBottomOffset(shouldSetOffset ? NAV_BAR_HEIGHT : 0)
	}

	connectedCallback() {
		super.connectedCallback()

		this.mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT)

		fromEvent<MediaQueryListEvent>(this.mobileMediaQuery, 'change')
			.pipe(
				tap(() => this.updateBottomOffset()),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		theme.fullscreen$
			.pipe(
				tap(isFullscreen => {
					this.isFullscreen = isFullscreen
					this.visibility$.next(!this.isFullscreen && !this.isHiddenByScroll)
					this.updateBottomOffset()
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.updateBottomOffset()

		fromEvent(this, 'bar-item-click')
			.pipe(
				tap((event: Event) => this.handleItemClick(event as CustomEvent)),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		fromEvent(this, 'keydown')
			.pipe(
				tap((event: Event) => this.handleKeyDown(event as KeyboardEvent)),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.activeIndex$.pipe(takeUntil(this.disconnecting)).subscribe(index => {
			this.updateActiveStates(index)
		})

		if (this.hideOnScroll) {
			this.setupScrollListener()
		}

		this.updateItems()
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		theme.setBottomOffset(0)
		this.mobileMediaQuery = null
	}

	private setupScrollListener() {
		fromEvent(window, 'scroll')
			.pipe(
				throttleTime(100),
				map(() => window.scrollY),
				pairwise(),
				filter(([prev, curr]) => Math.abs(curr - prev) > this.SCROLL_THRESHOLD),
				tap(([prev, curr]) => {
					const scrollingDown = curr > prev
					const scrollingUp = curr < prev
					const previousHiddenState = this.isHiddenByScroll

					if (scrollingDown && !this.isHiddenByScroll) {
						this.isHiddenByScroll = true
					} else if (scrollingUp && this.isHiddenByScroll) {
						this.isHiddenByScroll = false
					}

					if (curr <= this.SCROLL_THRESHOLD) {
						this.isHiddenByScroll = false
					}

					if (previousHiddenState !== this.isHiddenByScroll) {
						this.visibility$.next(!this.isHiddenByScroll && !this.isFullscreen)
					}
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	private handleItemClick = (event: CustomEvent) => {
		const items = this.getItems()
		const clickedItem = event.target as HTMLElement
		const index = items.findIndex(it => it === clickedItem)

		if (index === -1) return

		// Clicking the already-active item toggles the sidebar instead of re-navigating.
		if (this.activeIndex === index) {
			this.dispatchEvent(
				new CustomEvent(SchmancyEvents.NavDrawer_toggle, {
					detail: { state: 'toggle' },
					bubbles: true,
					composed: true,
				}),
			)
			return
		}

		const oldIndex = this.activeIndex
		this.activeIndex = index

		this.dispatchEvent(
			new CustomEvent('navigation-change', {
				detail: {
					oldIndex,
					newIndex: index,
					item: clickedItem,
				},
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleKeyDown = (event: KeyboardEvent) => {
		const items = this.getItems()
		const currentIndex = this.focusedIndex === -1 ? this.activeIndex : this.focusedIndex

		switch (event.key) {
			case 'ArrowLeft':
				event.preventDefault()
				if (currentIndex > 0) {
					this.focusItem(currentIndex - 1)
				}
				break

			case 'ArrowRight':
				event.preventDefault()
				if (currentIndex < items.length - 1) {
					this.focusItem(currentIndex + 1)
				}
				break

			case 'Home':
				event.preventDefault()
				this.focusItem(0)
				break

			case 'End':
				event.preventDefault()
				this.focusItem(items.length - 1)
				break

			case 'Enter':
			case ' ':
				event.preventDefault()
				if (this.focusedIndex !== -1) {
					items[this.focusedIndex]?.click()
				}
				break
		}
	}

	private focusItem(index: number) {
		const items = this.getItems()
		if (items[index]) {
			this.focusedIndex = index
			;(items[index] as HTMLElement).focus()
		}
	}

	private updateItems() {
		const slot = this.shadowRoot?.querySelector('slot')
		if (slot) {
			const handleSlotChange = () => {
				this.updateActiveStates(this.activeIndex)
			}
			slot.addEventListener('slotchange', handleSlotChange)
			handleSlotChange()
		}
	}

	public addBoatItem(config: { id: string; title: string; icon?: string }) {
		const existingItem = this.querySelector(`[value="${config.id}"]`) as HTMLElement
		if (existingItem) {
			return existingItem
		}

		const item = document.createElement('schmancy-navigation-bar-item')
		item.setAttribute('value', config.id)
		item.innerHTML = `
			<schmancy-icon>${config.icon || 'widgets'}</schmancy-icon>
			<span>${config.title}</span>
		`
		this.appendChild(item)
		return item
	}

	private updateActiveStates(activeIndex: number) {
		const items = this.getItems()
		items.forEach((item, index) => {
			item.setActive(index === activeIndex)
			item.hideLabels = this.hideLabels
			item.tabIndex = index === activeIndex ? 0 : -1
		})
	}

	// From here and never `updated()`: `isHiddenByScroll` is reactive, so
	// clearing it after render schedules a trailing update (Lit's
	// `change-in-update` warning); here it folds into the update in flight.
	// The scroll listener binds `window`, not this render's DOM.
	protected willUpdate(changedProperties: Map<string, unknown>) {
		super.willUpdate(changedProperties)
		if (changedProperties.has('hideOnScroll')) {
			if (this.hideOnScroll && !changedProperties.get('hideOnScroll')) {
				this.setupScrollListener()
			} else if (!this.hideOnScroll) {
				this.isHiddenByScroll = false
				this.visibility$.next(!this.isFullscreen)
			}
		}
	}

	updated(changedProperties: Map<string, unknown>) {
		super.updated(changedProperties)

		if (changedProperties.has('hideLabels')) {
			this.updateActiveStates(this.activeIndex)
		}
	}

	protected render() {
		const isVisuallyHidden = this.isFullscreen || this.isHiddenByScroll

		const containerClasses = {
			'h-20': true,
			'flex items-center justify-around': true,
			'px-2 py-3 box-border': true,
			'transition-all duration-300 ease-in-out': true,
			'z-10': true,
			'shadow-none': this.elevation === 0,
			'shadow-sm': this.elevation === 1,
			'shadow-md': this.elevation === 2,
			'shadow-lg': this.elevation === 3,
			'shadow-xl': this.elevation === 4,
			'shadow-2xl': this.elevation === 5,
		}

		const transformStyle = isVisuallyHidden ? 'translateY(100%)' : 'translateY(0)'

		return html`
			<nav
				class=${this.classMap(containerClasses)}
				role="navigation"
				aria-label="Main navigation"
				aria-hidden=${isVisuallyHidden}
				style="transform: ${transformStyle};"
				${color({
					bgColor: SchmancyTheme.sys.color.surface.container,
					color: SchmancyTheme.sys.color.surface.on,
				})}
			>
				<slot></slot>
			</nav>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-navigation-bar': SchmancyNavigationBar
	}
}
